/// <amd-module name="Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshActions" />
import { ActionsUnion, createAction } from "redux/Utils/ActionHelper";

export const TestHubRefreshActions = {
    /**
     * updateTestPlan: Dispatched when Test Plan is updated
     */
    updateTestPlan: (
        planId: number,
        testPlanName: string,
        testPlanStartDate: Date,
        testPlanFinishDate: Date,
        testPlanAreaPath: string,
        testPlanIterationPath: string
    ) =>
        createAction(TestHubRefreshActionsTypes.updateTestPlan, {
            planId,
            testPlanName,
            testPlanStartDate,
            testPlanFinishDate,
            testPlanAreaPath,
            testPlanIterationPath
        }),

    /**
     * setGridView: Dispatched when option to toggle between grid view or list view is selected.
     */
    setGridView: (isInGridView: boolean) => createAction(TestHubRefreshActionsTypes.setGridView, { isInGridView }),

    /**
     * updateBusyViewStatus: dispatched to handle rendering of spinner when a view is in busy state due to any user operation
     */
    updateBusyViewStatus: (isBusy: boolean) => createAction(TestHubRefreshActionsTypes.updateBusyViewStatus, { isBusy }),

    /**
     * dispatched to show the edit column options panel
     */
    editColumnOptionFieldsClicked: (planId: number, suiteId: number) =>
        createAction(TestHubRefreshActionsTypes.editColumnOptionFieldsClicked, { planId, suiteId }),

    /**
     * dispatched to update the state with the execute tab column options.
     */
    loadExecuteColumnOptionFieldsSucceeded: (executeColumnOptionFields: string[]) =>
        createAction(TestHubRefreshActionsTypes.loadExecuteColumnOptionFieldsSucceeded, { executeColumnOptionFields }),

    /**
     * dispatched to update the state with the define tab column options.
     */
    loadDefineColumnOptionFieldsSucceeded: (defineColumnOptionFields: string[]) =>
        createAction(TestHubRefreshActionsTypes.loadDefineColumnOptionFieldsSucceeded, { defineColumnOptionFields }),

    /**
     * dispatched to change the list view for the test suite to grid
     */
    showSuiteLevelGrid: (planId: number, suiteId: number, planName: string, areaPath: string, iterationPath: string) =>
        createAction(TestHubRefreshActionsTypes.showSuiteLevelGrid, { planId, suiteId, planName, areaPath, iterationPath }),

    /**
     * updatePagingStatus: dispatched to handle rendering of three dots paging indication
     */
    updatePagingStatus: (isPagingInProgress: boolean) => createAction(TestHubRefreshActionsTypes.updatePagingStatus, { isPagingInProgress }),

    /**
     * setIsGridDirty: Dispatched when grid is changed but not saved yet.
     */
    setIsGridDirty: (isGridDirty: boolean) => createAction(TestHubRefreshActionsTypes.setIsGridDirty, { isGridDirty }),

    // dispatched to persist define tab column widths in state
    changeDefineColumnWidths: (
        columnId: string | undefined,
        width: number | undefined,
        columnAndWidthObj: { [columnId: string]: number } | undefined
    ) => createAction(TestHubRefreshActionsTypes.changeDefineColumnWidths, { columnId, width, columnAndWidthObj }),

    // dispatched to persist execute tab column widths in state
    changeExecuteColumnWidths: (
        columnId: string | undefined,
        width: number | undefined,
        columnAndWidthObj: { [columnId: string]: number } | undefined
    ) => createAction(TestHubRefreshActionsTypes.changeExecuteColumnWidths, { columnId, width, columnAndWidthObj }),

    // dispatched to persist splitter width in state
    changeSplitterWidth: (splitterWidth: number) => createAction(TestHubRefreshActionsTypes.changeSplitterWidth, { splitterWidth }),

    /**
     * updateSelectedBuild: dispatched to update the build selected in test plan settings in the state
     */
    updateSelectedBuild: (selectedBuildId: string, selectedBuildDefnId: string) =>
        createAction(TestHubRefreshActionsTypes.updateSelectedBuild, { selectedBuildId, selectedBuildDefnId }),

    /**
     * updateSelectedRelease: dispatched to update the release selected in test plan settings in the state
     */
    updateSelectedRelease: (selectedReleaseId: number, selectedReleaseEnvironmentId: number) =>
        createAction(TestHubRefreshActionsTypes.updateSelectedRelease, { selectedReleaseId, selectedReleaseEnvironmentId }),
    /**
     * showTestsFromChildSuitesClick: Dispatched when show tests from child suites button is clicked
     */
    showTestsFromChildSuitesClick: (
        testPlanId: number,
        testSuiteId: number,
        isShowTestsFromChildSuitesEnabled: boolean,
        continuationToken?: string
    ) =>
        createAction(TestHubRefreshActionsTypes.showTestsFromChildSuitesClick, {
            testPlanId,
            testSuiteId,
            isShowTestsFromChildSuitesEnabled,
            continuationToken
        }),

    /**
     * loadMineAllPageUrl: dispatched to update the state with url after url is fetched
     */
    loadMineAllPageUrl: (mineAllPageUrl: string) => createAction(TestHubRefreshActionsTypes.loadMineAllPageUrl, { mineAllPageUrl }),

    /**
     * closeGrid: dispatched when grid view is closed via button, reload or tab change etc
     */
    closeGrid: (planId: number, selectedSuiteId: number) => createAction(TestHubRefreshActionsTypes.closeGrid, { planId, selectedSuiteId }),

    /**
     * dispatched to update the status (direction) of test case preview pane in db
     */
    saveTestCasePreviewStatus: (previewStatus: string) => createAction(TestHubRefreshActionsTypes.saveTestCasePreviewStatus, { previewStatus }),

    /**
     * dispatched to update the status (direction) of test case preview pane in testhubrefreshstate
     */
    changeTestCasePreviewStatus: (previewStatus: string) => createAction(TestHubRefreshActionsTypes.changeTestCasePreviewStatus, { previewStatus }),

    /**
     * dispatched to fetch the status (direction) of test case preview pane from db
     */
    fetchTestCasePreviewStatus: () => createAction(TestHubRefreshActionsTypes.fetchTestCasePreviewStatus, {}),

    /**
     * dispatched to update the mapping of custom column reference name and field name for define tab
     */
    updateDefineTabCustomColumnFieldMap: (customColumnFieldMap: { [columnId: string]: string }) =>
        createAction(TestHubRefreshActionsTypes.updateDefineTabCustomColumnFieldMap, { customColumnFieldMap }),

    /**
     * dispatched to update the mapping of custom column reference name and field name for execute tab
     */
    updateExecuteTabCustomColumnFieldMap: (customColumnFieldMap: { [columnId: string]: string }) =>
        createAction(TestHubRefreshActionsTypes.updateExecuteTabCustomColumnFieldMap, { customColumnFieldMap })
};

export const enum TestHubRefreshActionsTypes {
    updateTestPlan = "TestHubRefreshActions/updateTestPlan",
    setGridView = "TestHubRefreshActions/setGridView",
    setIsGridDirty = "TestHubRefreshActions/setIsGridDirty",
    closeGrid = "TestHubRefreshActions/closeGrid",
    updateBusyViewStatus = "TestHubRefreshActions/updateBusyViewStatus",
    editColumnOptionFieldsClicked = "TestHubRefreshActions/editColumnOptionFieldsClicked",
    loadExecuteColumnOptionFieldsSucceeded = "TestHubRefreshActions/loadExecuteColumnOptionFieldsSucceeded",
    loadDefineColumnOptionFieldsSucceeded = "TestHubRefreshActions/loadDefineColumnOptionFieldsSucceeded",
    updatePagingStatus = "TestHubRefreshActions/updatePagingStatus",
    showSuiteLevelGrid = "TestHubRefreshActions/showSuiteLevelGrid",
    changeDefineColumnWidths = "TestHubRefreshActions/changeDefineColumnWidths",
    changeExecuteColumnWidths = "TestHubRefreshActions/changeExecuteColumnWidths",
    changeSplitterWidth = "TestHubRefreshActions/changeSplitterWidth",
    updateSelectedBuild = "TestHubRefreshActions/updateSelectedBuild",
    updateSelectedRelease = "TestHubRefreshActions/updateSelectedRelease",
    showTestsFromChildSuitesClick = "Suites/showTestsFromChildSuitesClick",
    loadMineAllPageUrl = "TestHubRefreshActions/loadMineAllPageUrl",
    saveTestCasePreviewStatus = "TestHubRefreshActions/saveTestCasePreviewStatus",
    changeTestCasePreviewStatus = "TestHubRefreshActions/changeTestCasePreviewStatus",
    fetchTestCasePreviewStatus = "TestHubRefreshActions/fetchTestCasePreviewStatus",
    updateDefineTabCustomColumnFieldMap = "TestHubRefreshActions/updateDefineTabCustomColumnFieldMap",
    updateExecuteTabCustomColumnFieldMap = "TestHubRefreshActions/updateExecuteTabCustomColumnFieldMap"
}

export type TestHubRefreshActions = ActionsUnion<typeof TestHubRefreshActions>;
